import { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { useClientTranslation } from '@/lib/core/i18n';
import type { TranslationKey } from '@/lib/core/i18n/types';
import { useLocaleStore } from '@/lib/core/stores';
import { SheetHeader } from '@/components/shared/shop/ui/SheetHeader';
import { FilterCheckboxItem } from '@/components/shared/shop/product/list/filters/sheet/checkbox-item';
import type { EventVenueFacetDto } from '@/services/platform/api/model';
import {
  getEvents,
  visibilityParam,
  EVENT_DATE_PRESET_ORDER,
  type EventDatePresetToken,
} from '@/services/platform/data/events';
import { brandifyJncquoi } from '@/lib/core/utils/text';

/** Debounce for the "Show N results" live preview fetch, triggered on every draft change. */
const PREVIEW_DEBOUNCE_MS = 300;

const DATE_PRESET_LABEL_KEYS: Record<EventDatePresetToken, TranslationKey> = {
  today: 'App.Events.Filter.Today',
  tomorrow: 'App.Events.Filter.Tomorrow',
  thisWeek: 'App.Events.Filter.ThisWeek',
  nextWeek: 'App.Events.Filter.NextWeek',
  everyday: 'App.Events.Filter.Everyday',
  mondayToFriday: 'App.Events.Filter.MondayToFriday',
  weekend: 'App.Events.Filter.EveryWeekend',
  sunday: 'App.Events.Filter.EverySunday',
};

export interface EventsFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Scopes the venue list and the live result-count preview to the active hub. */
  hubKey?: string;
  venues: EventVenueFacetDto[];
  selectedVenueIds: string[];
  selectedDatePresets: EventDatePresetToken[];
  selectedMembersOnly: boolean;
  /** Commits the draft selection to the Events Calendar and closes the sheet. */
  onApply: (selection: EventsFilterSelection) => void;
  /** Resets the Events Calendar's venue/date-interval/visibility filters immediately (sheet stays open). */
  onClearAll: () => void;
}

/** What the sheet commits on "Show results". */
export interface EventsFilterSelection {
  venueIds: string[];
  datePresets: EventDatePresetToken[];
  membersOnly: boolean;
}

/**
 * ELP filter overlay — Venue, Date interval and Visibility ("Members only"), each an independently
 * expandable/collapsible checkbox section (Figma "ELP | Filter Overlay v1").
 * Modeled on `ShopProductListFilterSheet`'s draft-state pattern: selections
 * are staged locally and only committed to the calendar via "Show results"
 * (or immediately via "Clear all", which resets the calendar right away).
 */
export function EventsFilterSheet({
  open,
  onOpenChange,
  hubKey,
  venues,
  selectedVenueIds,
  selectedDatePresets,
  selectedMembersOnly,
  onApply,
  onClearAll,
}: EventsFilterSheetProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);

  const [draftVenueIds, setDraftVenueIds] = useState<string[]>(selectedVenueIds);
  const [draftDatePresets, setDraftDatePresets] = useState<EventDatePresetToken[]>(selectedDatePresets);
  const [draftMembersOnly, setDraftMembersOnly] = useState(selectedMembersOnly);

  // Drafts mirror the committed selection and only resync on a fresh open.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (justOpened) {
      setDraftVenueIds(selectedVenueIds);
      setDraftDatePresets(selectedDatePresets);
      setDraftMembersOnly(selectedMembersOnly);
    }
  }, [open, selectedVenueIds, selectedDatePresets, selectedMembersOnly]);

  const toggleVenue = useCallback((id: string) => {
    setDraftVenueIds(prev => (prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]));
  }, []);

  const toggleDatePreset = useCallback((token: EventDatePresetToken) => {
    setDraftDatePresets(prev => (prev.includes(token) ? prev.filter(p => p !== token) : [...prev, token]));
  }, []);

  const hasSelection = draftVenueIds.length > 0 || draftDatePresets.length > 0 || draftMembersOnly;
  const selectedCount = draftVenueIds.length + draftDatePresets.length + (draftMembersOnly ? 1 : 0);

  // Live "Show N results" preview: debounced pageSize=1 fetch for just the total count.
  const [previewCount, setPreviewCount] = useState(0);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewRequestIdRef = useRef(0);

  useEffect(() => {
    if (!hasSelection) {
      setPreviewCount(0);
      setIsPreviewLoading(false);
      return;
    }

    const requestId = ++previewRequestIdRef.current;
    setIsPreviewLoading(true);

    const timer = setTimeout(() => {
      // No `isCampaign` — the ELP renders dated events and campaigns alike, so the preview must
      // count both. `includeRestricted` as the list itself sends it: without it the count leaves
      // out the Members Only/Invite Only cards the list shows (and "Members only" alone counts 0).
      getEvents(
        {
          hubKey,
          venueId: draftVenueIds,
          datePreset: draftDatePresets,
          visibility: visibilityParam(draftMembersOnly),
          includeRestricted: true,
          pageSize: 1,
        },
        locale
      ).then(result => {
        if (requestId !== previewRequestIdRef.current) return; // superseded by a later draft change
        setPreviewCount(result?.totalItems ?? 0);
        setIsPreviewLoading(false);
      });
    }, PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [hasSelection, draftVenueIds, draftDatePresets, draftMembersOnly, hubKey, locale]);

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleShowResults = useCallback(() => {
    onApply({ venueIds: draftVenueIds, datePresets: draftDatePresets, membersOnly: draftMembersOnly });
    onOpenChange(false);
  }, [draftVenueIds, draftDatePresets, draftMembersOnly, onApply, onOpenChange]);

  const handleClearAll = useCallback(() => {
    setDraftVenueIds([]);
    setDraftDatePresets([]);
    setDraftMembersOnly(false);
    onClearAll();
  }, [onClearAll]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
      <DrawerContent className="flex max-h-[85dvh] flex-col overflow-hidden">
        <SheetHeader
          title={t('App.Events.Filter.Title')}
          right={
            <button
              type="button"
              aria-label={t('App.Labels.Close')}
              onClick={handleClose}
              className="flex size-11 cursor-pointer items-center justify-center"
            >
              <Icon name="xmark" width={24} height={24} className="text-jncquoi-neutral-1000" />
            </button>
          }
        />

        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto px-4 pb-4">
          <Accordion type="multiple" className="border-jncquoi-neutral-900 border-b-[0.5px]">
            <AccordionItem value="venue">
              <AccordionTrigger>{t('App.Events.Filter.Venue')}</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-x-3 gap-y-4 pt-2 pb-6">
                  {venues.map(venue => {
                    const id = venue.id;
                    if (!id) return null;
                    return (
                      <FilterCheckboxItem
                        key={id}
                        id={`filter-venue-${id}`}
                        label={brandifyJncquoi(venue.name?.toUpperCase() ?? '')}
                        checked={draftVenueIds.includes(id)}
                        onChange={() => toggleVenue(id)}
                        className="[&_span]:normal-case!"
                      />
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="date-interval">
              <AccordionTrigger>{t('App.Events.Filter.DateInterval')}</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-x-3 gap-y-4 pt-2 pb-6">
                  {EVENT_DATE_PRESET_ORDER.map(token => (
                    <FilterCheckboxItem
                      key={token}
                      id={`filter-date-preset-${token}`}
                      label={t(DATE_PRESET_LABEL_KEYS[token])}
                      checked={draftDatePresets.includes(token)}
                      onChange={() => toggleDatePreset(token)}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="visibility">
              <AccordionTrigger>{t('App.Events.Filter.Visibility')}</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-x-3 gap-y-4 pt-2 pb-6">
                  <FilterCheckboxItem
                    id="filter-visibility-members-only"
                    label={t('App.Labels.MembersOnly')}
                    checked={draftMembersOnly}
                    onChange={() => setDraftMembersOnly(prev => !prev)}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="border-jncquoi-neutral-400 flex shrink-0 flex-col gap-2 border-t-[0.5px] px-4 pt-6 pb-10.5">
          <Button
            variant={hasSelection ? 'primary-solid' : 'secondary-outline'}
            size="lg"
            onClick={handleShowResults}
            // A 0-result selection stays applicable — the ELP has a "no results" state for it,
            // and blocking Apply left the user stuck with no way to commit or see why.
            disabled={!hasSelection || isPreviewLoading}
            className="w-full"
          >
            {!hasSelection
              ? t('App.Events.Filter.ShowResults')
              : isPreviewLoading
                ? t('App.Labels.Loading')
                : t('App.Events.Filter.ShowResultsCount', { count: previewCount })}
          </Button>
          <Button
            variant="tertiary-link"
            size="sm"
            onClick={handleClearAll}
            disabled={!hasSelection}
            className="w-full"
          >
            {t('App.Events.Filter.ClearAllCount', { count: selectedCount })}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
